// src/pages/SettingsPage.js
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { settings } from '../utils/api';
import Pagination, { usePagination } from '../components/Pagination';
import AdminsPage from './AdminsPage';

function Section({ title, addForm, list }) {
  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">Add {title}</span></div>
        {addForm}
      </div>
      <div className="card">
        <div className="card-header"><span className="card-title">Existing {title}s</span></div>
        {list}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [tab, setTab] = useState('general');
  const qc = useQueryClient();

  const [maxDevices, setMaxDevices] = useState('');

  const [newCourse, setNewCourse] = useState({ name: '', code: '', department: '' });
  const [newInst,   setNewInst]   = useState({ name: '', code: '', type: 'faculty' });
  const [newYear,   setNewYear]   = useState({ period: '', year_number: '' });

  const [editingCourse, setEditingCourse] = useState(null);
  const [editingInst,   setEditingInst]   = useState(null);
  const [editingYear,   setEditingYear]   = useState(null);

  const { data: courses }      = useQuery({ queryKey: ['courses'],      queryFn: () => settings.courses().then(r => r.data.data) });
  const { data: institutions } = useQuery({ queryKey: ['institutions'], queryFn: () => settings.institutions().then(r => r.data.data) });
  const { data: years }        = useQuery({ queryKey: ['years'],        queryFn: () => settings.academicYears().then(r => r.data.data) });
  const { data: appSettings }  = useQuery({ queryKey: ['app-settings'], queryFn: () => settings.appSettings().then(r => r.data.data) });

  useEffect(() => {
    if (appSettings && maxDevices === '') {
      setMaxDevices(appSettings.max_devices_per_student ?? '2');
    }
  }, [appSettings, maxDevices]);

  const { paginated: coursesPaged, page: cPage, setPage: setCPage, totalPages: cTP } = usePagination(courses || [], 10);
  const { paginated: instPaged,    page: iPage, setPage: setIPage, totalPages: iTP } = usePagination(institutions || [], 10);
  const { paginated: yearsPaged,   page: yPage, setPage: setYPage, totalPages: yTP } = usePagination(years || [], 10);

  const onErr = err => toast.error(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Operation failed');

  const addCourseMut    = useMutation({ mutationFn: d => settings.createCourse(d),           onSuccess: () => { toast.success('Course added');       qc.invalidateQueries(['courses']);      setNewCourse({ name:'', code:'', department:'' }); }, onError: onErr });
  const updateCourseMut = useMutation({ mutationFn: ({id,d}) => settings.updateCourse(id,d), onSuccess: () => { toast.success('Course updated');    qc.invalidateQueries(['courses']);      setEditingCourse(null); }, onError: onErr });
  const delCourseMut    = useMutation({ mutationFn: id => settings.deleteCourse(id),         onSuccess: () => { toast.success('Course removed');     qc.invalidateQueries(['courses']); },  onError: onErr });

  const addInstMut      = useMutation({ mutationFn: d => settings.createInst(d),             onSuccess: () => { toast.success('School added');   qc.invalidateQueries(['institutions']); setNewInst({ name:'', code:'', type:'faculty' }); }, onError: onErr });
  const updateInstMut   = useMutation({ mutationFn: ({id,d}) => settings.updateInst(id,d),   onSuccess: () => { toast.success('School updated'); qc.invalidateQueries(['institutions']); setEditingInst(null); }, onError: onErr });
  const delInstMut      = useMutation({ mutationFn: id => settings.deleteInst(id),           onSuccess: () => { toast.success('School removed'); qc.invalidateQueries(['institutions']); }, onError: onErr });

  const addYearMut      = useMutation({ mutationFn: d => settings.createYear(d),             onSuccess: () => { toast.success('Year added');          qc.invalidateQueries(['years']);        setNewYear({ period:'', year_number:'' }); }, onError: onErr });
  const updateYearMut   = useMutation({ mutationFn: ({id,d}) => settings.updateYear(id,d),   onSuccess: () => { toast.success('Year updated');        qc.invalidateQueries(['years']);        setEditingYear(null); }, onError: onErr });
  const delYearMut      = useMutation({ mutationFn: id => settings.deleteYear(id),           onSuccess: () => { toast.success('Year removed');        qc.invalidateQueries(['years']); },    onError: onErr });

  const saveAppSettingsMut = useMutation({
    mutationFn: d => settings.updateAppSettings(d),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries(['app-settings']); },
    onError: onErr
  });

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>

      <div className="tabs">
        {[
          ['general', 'Device Limit'],
          ['courses', 'Courses'],
          ['institutions', 'Schools'],
          ['years', 'Years'],
          ['admins', 'Admin Users']
        ].map(([t, label]) => (
          <div key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{label}</div>
        ))}
      </div>

      {tab === 'admins' && <AdminsPage />}

      {tab === 'general' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Device Limits</span></div>
          <div style={{ padding: '4px 0 12px', maxWidth: 420 }}>
            <div className="form-group">
              <label className="form-label">Maximum devices per student</label>
              <input
                className="form-control"
                type="number"
                min="1"
                max="50"
                value={maxDevices}
                onChange={e => setMaxDevices(e.target.value)}
              />
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
                Once a student reaches this many registered devices, admins must remove or disable
                an existing device before a new one can be registered.
              </div>
            </div>
            <button
              className="btn btn-primary btn-sm"
              disabled={!maxDevices || saveAppSettingsMut.isPending}
              onClick={() => {
                if (window.confirm(`Set the maximum devices per student to ${maxDevices}? This applies immediately across the whole system.`)) {
                  saveAppSettingsMut.mutate({ max_devices_per_student: parseInt(maxDevices, 10) });
                }
              }}
            >
              {saveAppSettingsMut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {tab === 'courses' && (
        <Section title="Course"
          addForm={
            <div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Course Name</label>
                  <input className="form-control" placeholder="e.g. BSc in Nursing" value={newCourse.name} onChange={e => setNewCourse(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Code</label>
                  <input className="form-control" placeholder="e.g. BSC-NRS" value={newCourse.code} onChange={e => setNewCourse(f => ({ ...f, code: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Department</label>
                  <input className="form-control" placeholder="e.g. School of Nursing" value={newCourse.department} onChange={e => setNewCourse(f => ({ ...f, department: e.target.value }))} />
                </div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => addCourseMut.mutate(newCourse)} disabled={!newCourse.name || !newCourse.code}>+ Add Course</button>
            </div>
          }
          list={
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Code</th><th>Department</th><th></th></tr></thead>
                <tbody>
                  {coursesPaged.map(c => editingCourse?.id === c.id ? (
                    <tr key={c.id}>
                      <td><input className="form-control" value={editingCourse.name} onChange={e => setEditingCourse(f => ({ ...f, name: e.target.value }))} /></td>
                      <td><input className="form-control" value={editingCourse.code} onChange={e => setEditingCourse(f => ({ ...f, code: e.target.value }))} /></td>
                      <td><input className="form-control" value={editingCourse.department || ''} onChange={e => setEditingCourse(f => ({ ...f, department: e.target.value }))} /></td>
                      <td style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => updateCourseMut.mutate({ id: editingCourse.id, d: editingCourse })}>Save</button>
                        <button className="btn btn-sm" onClick={() => setEditingCourse(null)}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td><span className="badge badge-primary">{c.code}</span></td>
                      <td>{c.department || '—'}</td>
                      <td style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-sm" onClick={() => setEditingCourse({ ...c })}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { if(window.confirm('Remove this course?')) delCourseMut.mutate(c.id); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination page={cPage} totalPages={cTP} onChange={setCPage} />
            </div>
          }
        />
      )}

      {tab === 'institutions' && (
        <Section title="School"
          addForm={
            <div>
              <div className="form-grid-3">
                <div className="form-group">
                  <label className="form-label">Name</label>
                  <input className="form-control" placeholder="e.g. School of Medicine" value={newInst.name} onChange={e => setNewInst(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Code</label>
                  <input className="form-control" placeholder="e.g. MED" value={newInst.code} onChange={e => setNewInst(f => ({ ...f, code: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Type</label>
                  <select className="form-control" value={newInst.type} onChange={e => setNewInst(f => ({ ...f, type: e.target.value }))}>
                    <option value="faculty">Faculty</option>
                    <option value="college">College</option>
                    <option value="school">School</option>
                    <option value="department">Department</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => addInstMut.mutate(newInst)} disabled={!newInst.name || !newInst.code}>+ Add School</button>
            </div>
          }
          list={
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Code</th><th>Type</th><th></th></tr></thead>
                <tbody>
                  {instPaged.map(i => editingInst?.id === i.id ? (
                    <tr key={i.id}>
                      <td><input className="form-control" value={editingInst.name} onChange={e => setEditingInst(f => ({ ...f, name: e.target.value }))} /></td>
                      <td><input className="form-control" value={editingInst.code} onChange={e => setEditingInst(f => ({ ...f, code: e.target.value }))} /></td>
                      <td>
                        <select className="form-control" value={editingInst.type} onChange={e => setEditingInst(f => ({ ...f, type: e.target.value }))}>
                          <option value="faculty">Faculty</option>
                          <option value="college">College</option>
                          <option value="school">School</option>
                          <option value="department">Department</option>
                          <option value="other">Other</option>
                        </select>
                      </td>
                      <td style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => updateInstMut.mutate({ id: editingInst.id, d: editingInst })}>Save</button>
                        <button className="btn btn-sm" onClick={() => setEditingInst(null)}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={i.id}>
                      <td>{i.name}</td>
                      <td><span className="badge badge-info">{i.code}</span></td>
                      <td style={{ textTransform: 'capitalize' }}>{i.type}</td>
                      <td style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-sm" onClick={() => setEditingInst({ ...i })}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { if(window.confirm('Remove this school?')) delInstMut.mutate(i.id); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination page={iPage} totalPages={iTP} onChange={setIPage} />
            </div>
          }
        />
      )}

      {tab === 'years' && (
        <Section title="Academic Year"
          addForm={
            <div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Academic Period (e.g. 2025/2026)</label>
                  <input className="form-control" placeholder="2025/2026" value={newYear.period} onChange={e => setNewYear(f => ({ ...f, period: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Year of Study (1–6)</label>
                  <input className="form-control" type="number" min="1" max="10" placeholder="1" value={newYear.year_number} onChange={e => setNewYear(f => ({ ...f, year_number: e.target.value }))} />
                </div>
              </div>
              {newYear.period && newYear.year_number && (
                <div style={{ marginBottom: 10, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Label will be: <strong>{newYear.period} - Y{newYear.year_number}</strong>
                </div>
              )}
              <button className="btn btn-primary btn-sm"
                onClick={() => addYearMut.mutate({ label: `${newYear.period} - Y${newYear.year_number}`, year_number: newYear.year_number })}
                disabled={!newYear.period || !newYear.year_number}>
                + Add Year
              </button>
            </div>
          }
          list={
            <div className="table-wrap">
              <table>
                <thead><tr><th>Label</th><th>Year #</th><th>Current?</th><th></th></tr></thead>
                <tbody>
                  {yearsPaged.map(y => editingYear?.id === y.id ? (
                    <tr key={y.id}>
                      <td><input className="form-control" value={editingYear.label} onChange={e => setEditingYear(f => ({ ...f, label: e.target.value }))} /></td>
                      <td><input className="form-control" type="number" min="1" max="10" value={editingYear.year_number} onChange={e => setEditingYear(f => ({ ...f, year_number: e.target.value }))} /></td>
                      <td>
                        <select className="form-control" value={editingYear.is_current ? '1' : '0'} onChange={e => setEditingYear(f => ({ ...f, is_current: e.target.value === '1' }))}>
                          <option value="0">No</option>
                          <option value="1">Yes</option>
                        </select>
                      </td>
                      <td style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => updateYearMut.mutate({ id: editingYear.id, d: editingYear })}>Save</button>
                        <button className="btn btn-sm" onClick={() => setEditingYear(null)}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={y.id}>
                      <td>{y.label}</td>
                      <td>Year {y.year_number}</td>
                      <td>{y.is_current ? <span className="badge badge-success">Current</span> : '—'}</td>
                      <td style={{ display:'flex', gap:6 }}>
                        <button className="btn btn-sm" onClick={() => setEditingYear({ ...y })}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => { if(window.confirm('Remove this year?')) delYearMut.mutate(y.id); }}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination page={yPage} totalPages={yTP} onChange={setYPage} />
            </div>
          }
        />
      )}
    </div>
  );
}
